import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";
/**
 * Button variants following the Diality design system.
 *
 * UX Notes:
 * - Primary and outline CTAs share a 49px pill height.
 * - Arrow controls use the prototype's compact 80×32px form.
 * - `compact` / `xs` are dense admin sizes for the CMS (table rows, toolbars,
 *   pagination). They keep the pill radius but drop the hover lift.
 * - cursor-pointer on all clickable elements
 * - Hover transitions: 200ms ease, no delay
 * - Disabled: reduced opacity 0.5 + cursor change
 * - Focus: visible ring for keyboard nav
 */
const buttonVariants = cva([
    "inline-flex items-center justify-center gap-2",
    "whitespace-nowrap font-body font-light leading-[1.04] tracking-[-0.02em]",
    "transition-[background-color,border-color,color,transform,translate] duration-200 ease-[ease] delay-0",
    "cursor-pointer select-none",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
].join(" "), {
    variants: {
        variant: {
            default: "rounded-full bg-primary text-primary-foreground hover:bg-brand-deep-blue group-hover/button:bg-brand-deep-blue",
            destructive: "rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 group-hover/button:bg-destructive/90 active:bg-destructive/80 group-active/button:bg-destructive/80",
            outline: "rounded-full border-1 border-primary bg-transparent text-primary hover:border-primary group-hover/button:border-primary hover:bg-primary group-hover/button:bg-primary hover:text-primary-foreground group-hover/button:text-primary-foreground",
            secondary: "rounded-full border border-border bg-secondary text-secondary-foreground hover:bg-secondary/80 group-hover/button:bg-secondary/80",
            ghost: "rounded-full hover:bg-accent group-hover/button:bg-accent hover:text-accent-foreground group-hover/button:text-accent-foreground",
            link: "min-h-0 rounded-none p-0 font-sans text-sm font-black uppercase tracking-[0.1em] text-primary hover:gap-3 group-hover/button:gap-3 hover:text-brand-deep-blue group-hover/button:text-brand-deep-blue",
            accent: "rounded-full border border-border bg-accent text-accent-foreground hover:bg-accent/90 group-hover/button:bg-accent/90 active:bg-accent/80 group-active/button:bg-accent/80",
            arrow: "rounded-2xl bg-primary text-primary-foreground hover:bg-brand-deep-blue group-hover/button:bg-brand-deep-blue active:scale-[0.94] group-active/button:scale-[0.94]",
            "arrow-outline": "rounded-2xl border border-primary bg-transparent text-primary hover:bg-primary group-hover/button:bg-primary hover:text-primary-foreground group-hover/button:text-primary-foreground",
        },
        size: {
            default: "h-[49px] min-h-[49px] px-6 py-0 text-base",
            sm: "min-h-11 px-5 py-3 text-sm",
            lg: "min-h-[52px] px-8 py-4 text-base",
            icon: "size-11 rounded-full p-0",
            arrow: "h-8 w-20 rounded-2xl p-0 [&_svg]:h-2 [&_svg]:w-[49px]",
            /**
             * Dense admin sizes (added for the CMS; the marketing site uses none of
             * them, so nothing existing moves). Two deliberate overrides:
             *   - font-medium beats the base's font-light — 300 is right for a 49px
             *     CTA and too thin for a 32px control.
             *   - compact sizes omit the surface lift. A row-action
             *     button that hops on hover is noise, not affordance.
             * The pill radius is kept: at this height a full round is the site's own
             * .arrow-btn lozenge, which is the brand-correct dense control.
             */
            compact: "min-h-9 px-4 py-2 text-sm font-medium",
            xs: "min-h-8 px-3.5 py-1.5 text-xs font-medium",
        },
    },
    defaultVariants: {
        variant: "default",
        size: "default",
    },
});
// Layout and focus belong to the interactive element. Its child paints the
// pill, so lifting that child never moves the hit area out from under the mouse.
const Button = React.forwardRef(({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const child = asChild
        ? React.Children.only(children)
        : null;
    const classes = cn(buttonVariants({ variant, size }), className, child?.props.className);
    const lifts = size !== "compact" &&
        size !== "xs" &&
        ["default", "destructive", "outline", "secondary", "accent"].includes(variant ?? "default");
    const surface = (_jsx("span", { "data-button-surface": "", className: cn(classes, lifts &&
            "group-hover/button:-translate-y-0.5 group-active/button:translate-y-0"), children: child ? child.props.children : children }));
    return (_jsx(Comp, { className: cn("btn group/button", classes), ref: ref, ...props, children: child
            ? React.cloneElement(child, {
                children: surface,
            })
            : surface }));
});
Button.displayName = "Button";
export { Button, buttonVariants };
//# sourceMappingURL=button.js.map